import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import imageCompression from 'browser-image-compression';

const MAX_FILE_SIZE_MB = 5;

const ExpenseUpload = () => {
  const [file, setFile] = useState(null);
  const [compressedFile, setCompressedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    date: '',
    amount: '',
    vendor: '',
    description: '',
  });

  useEffect(() => {
    const analyzeFile = async () => {
      if (!compressedFile) return;

      setError('');
      setPreviewData(null);
      setFormValues({ date: '', amount: '', vendor: '', description: '' });

      const sizeInMB = compressedFile.size / (1024 * 1024);
      if (sizeInMB > MAX_FILE_SIZE_MB) {
        setError('File size must be less than 5 MB.');
        return;
      }

      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('file', compressedFile);

        const res = await axios.post('/api/expense/analyze', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const { date, amount, vendor, description } = res.data;
        setPreviewData(res.data);
        setFormValues({ date, amount, vendor, description });
      } catch (err) {
        setError('Failed to analyze receipt.');
        console.error(err);
      }
      setLoading(false);
    };

    analyzeFile();
  }, [compressedFile]);

  const handleFileChange = async (e) => {
    const rawFile = e.target.files[0];
    setFile(rawFile);
    setError('');

    try {
      const compressed = await imageCompression(rawFile, {
        maxSizeMB: 1, // ~100KB
        maxWidthOrHeight: 1080,
        useWebWorker: true,
      });
      setCompressedFile(compressed);
    } catch (err) {
      console.error('Compression failed:', err);
      setError('Compression failed. Please try another file.');
    }
  };

  const handleChange = (e) => {
    setFormValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post('/api/expenses', formValues);
      if (response) {
        alert('Expense saved successfully!');
        setFile(null);
        setPreviewData(null);
        setCompressedFile(null);
        setFormValues(response.data);
      }
    } catch (err) {
      setError('Failed to save expense.');
      console.error(err);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Upload Expense Receipt
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <input
        type="file"
        accept="image/*,.pdf"
        onChange={handleFileChange}
        style={{ marginBottom: '1rem' }}
      />

      {loading && <CircularProgress sx={{ mb: 2 }} />}

      {previewData && (
        <Box sx={{ display: 'flex', gap: 3, mt: 2, alignItems: 'flex-start' }}>
          {/* Left: Image preview */}
          <Box sx={{ flex: 1, maxWidth: 300 }}>
            {file && file.type.startsWith('image') ? (
              <img
                src={URL.createObjectURL(compressedFile)}
                alt="Receipt Preview"
                style={{ width: '100%', borderRadius: 4 }}
              />
            ) : (
              <Typography variant="body2">PDF preview not supported</Typography>
            )}
            {compressedFile && (
              <Typography variant="caption" color="text.secondary" mt={1}>
                Compressed: {(compressedFile.size / 1024).toFixed(1)} KB
              </Typography>
            )}
          </Box>

          {/* Right: Form */}
          <Paper sx={{ flex: 2, p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Confirm Expense Details
            </Typography>

            <TextField
              fullWidth
              label="Date"
              name="date"
              type="date"
              value={formValues.date}
              onChange={handleChange}
              margin="dense"
            />
            <TextField
              fullWidth
              label="Amount"
              name="amount"
              type="number"
              value={formValues.amount}
              onChange={handleChange}
              margin="dense"
            />
            <TextField
              fullWidth
              label="Vendor"
              name="vendor"
              type="text"
              value={formValues.vendor}
              onChange={handleChange}
              margin="dense"
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formValues.description}
              onChange={handleChange}
              margin="dense"
            />

            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              sx={{ mt: 2 }}
            >
              Save to DB
            </Button>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ExpenseUpload;
